import React, { useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Users } from "lucide-react";

export function BoardStats({ volunteers, assignments }) {
  const { rows, avg } = useMemo(() => {
    const rows = volunteers.map((v) => {
      const mine = assignments.filter((a) => a.user_id === v.id);
      const byType = {};
      mine.forEach((a) => { const n = a.service_type_name || "Overig"; byType[n] = (byType[n] || 0) + 1; });
      return { id: v.id, name: v.name, total: mine.length, byType, max: v.max_shifts_per_month || null, pct: 0 };
    });
    const withShifts = rows.filter((r) => r.total > 0);
    const average = withShifts.length ? rows.reduce((s, r) => s + r.total, 0) / withShifts.length : 0;
    rows.forEach((r) => { r.deviation = average > 0 ? Math.round((r.total / average) * 100 - 100) : 0; });
    rows.sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
    return { rows, avg: average };
  }, [volunteers, assignments]);

  const devTone = (dev) => dev > 0 ? "text-amber-600" : dev < 0 ? "text-sky-600" : "text-muted-foreground";

  return (
    <Card className="p-4" data-testid="board-stats">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading font-semibold flex items-center gap-2"><Users className="w-4 h-4 text-red-600" /> Medewerkers</h2>
        <span className="text-xs text-muted-foreground" data-testid="board-stats-avg" title="Groepsgemiddelde diensten">Gem. {avg ? avg.toFixed(1) : "0"}</span>
      </div>
      <div className="space-y-2 max-h-[60vh] lg:max-h-[calc(100vh-16rem)] overflow-y-auto pr-1">
        {rows.length === 0 && <p className="text-xs text-muted-foreground">Geen actieve medewerkers.</p>}
        {rows.map((r) => (
          <div key={r.id} className="rounded-lg border border-slate-200 dark:border-slate-700 p-2.5" data-testid={`board-stat-${r.id}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-sm truncate">{r.name}</span>
              <span className={`text-xs font-bold shrink-0 ${devTone(r.deviation)}`} data-testid={`board-stat-pct-${r.id}`} title="Afwijking t.o.v. groepsgemiddelde">{r.deviation > 0 ? "+" : ""}{r.deviation}%</span>
            </div>
            <div className="flex items-center justify-between mt-1 text-xs">
              <span className="text-muted-foreground">Totaal: <span className="font-semibold text-foreground" data-testid={`board-stat-total-${r.id}`}>{r.total}</span></span>
              <span className="text-muted-foreground" data-testid={`board-stat-max-${r.id}`}>
                {r.max ? <>Max: <span className={`font-semibold ${r.total > r.max ? "text-amber-600" : "text-foreground"}`}>{r.total} / {r.max}</span></> : <span className="italic">geen max</span>}
              </span>
            </div>
            {Object.keys(r.byType).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`board-stat-types-${r.id}`}>
                {Object.entries(r.byType).sort((a, b) => a[0].localeCompare(b[0])).map(([type, n]) => (
                  <span key={type} className="text-[11px] bg-muted px-1.5 py-0.5 rounded-full">{type} <span className="font-semibold">{n}</span></span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}
